import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import apiPath from "../../constants/apiPath";
import logoImg from "../../assets/images/logo-for-logins.png";

const PORTAL_BASE = "https://portal.fusionhub.studio/api/auth";

// SVG Icons (inline, no dependency needed)
const Icon = ({ d, size = 20, color = "currentColor" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
    <path d={d} />
  </svg>
);
const ICONS = {
  tv:       "M21 3H3a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h5v2h8v-2h5a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2z",
  zap:      "M13 2L3 14h9l-1 8 10-12h-9l1-8z",
  timer:    "M10 2h4M12 2v3M12 5a7 7 0 1 0 0 14 7 7 0 0 0 0-14zM12 8v4l3 3",
  layout:   "M3 3h18v4H3zM3 11h8v10H3zM15 11h6v10h-6z",
  wifi:     "M5 12.55a11 11 0 0 1 14.08 0M1.42 9a16 16 0 0 1 21.16 0M8.53 16.11a6 6 0 0 1 6.95 0M12 20h.01",
  phone:    "M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z",
  users:    "M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75",
  star:     "M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z",
  calendar: "M3 4h18M3 8h18M8 2v4M16 2v4M3 4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h18a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2H3zM7 12h2M11 12h2M15 12h2M7 16h2M11 16h2",
  bar:      "M18 20V10M12 20V4M6 20v-6",
  tablet:   "M7 2h10a2 2 0 0 1 2 2v16a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2zM12 18h.01",
  check:    "M20 6L9 17l-5-5",
  arrow:    "M5 12h14M12 5l7 7-7 7",
  close:    "M18 6L6 18M6 6l12 12",
  eye:      "M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8zM12 9a3 3 0 1 0 0 6 3 3 0 0 0 0-6z",
  cpu:      "M18 4H6a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2zM9 9h6v6H9z",
  globe:    "M2 12a10 10 0 1 0 20 0 10 10 0 0 0-20 0zM12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10zM2 12h20",
  android:  "M17.523 15.341L18 12.5l.477 2.841a2 2 0 1 1-3.954 0L15 12.5l.477 2.841a1 1 0 0 1-1.954 0L13 12.5l.477 2.841M12 2C6.477 2 2 6.477 2 12s4.477 10 10 10 10-4.477 10-10S17.523 2 12 2z",
  download: "M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3",
};

const STATS = [
  { value: "8", label: "Display modes" },
  { value: "<50ms", label: "Sync latency" },
  { value: "∞", label: "TVs per gym" },
  { value: "0", label: "Apps for members" },
];

const FEATURES = [
  {
    icon: "layout",
    title: "8 Display Modes",
    desc: "Hero screen, exercise table, countdown timer, HYROX split layout, rep counter, video fullscreen, transition, and completion screens - each built for a specific moment in class.",
  },
  {
    icon: "wifi",
    title: "Real-Time WebSocket Sync",
    desc: "Push live updates to every screen in under 50ms. Start timers, switch blocks, trigger transitions from your phone or tablet without touching the TV.",
  },
  {
    icon: "cpu",
    title: "A / B / C Workout Variants",
    desc: "Display scaled and advanced variations simultaneously. Members choose their level while coaches run one session. Scaling happens on screen.",
  },
  {
    icon: "timer",
    title: "Live Countdown Timers",
    desc: "Work, rest, AMRAP, EMOM. Set your interval format and broadcast it. Timer colour shifts green to amber to red automatically as time expires.",
  },
  {
    icon: "tv",
    title: "HYROX & Functional Mode",
    desc: "Split-screen layout designed for HYROX and functional fitness. Elapsed timer on the left, live station list on the right. No manual updates.",
  },
  {
    icon: "calendar",
    title: "Weekly Programme View",
    desc: "Coaches and members see the week ahead on any device with no login needed. Share your gym code and the programme is instantly accessible.",
  },
];

const APP_FEATURES = [
  "Native Android TV & Amazon Fire TV app",
  "6-character gym code login, D-pad friendly",
  "Offline mode with cached programme fallback",
  "Auto-reconnects on network drop",
  "30-day session, no daily login",
  "All 8 display modes, pixel-perfect on 4K",
];

const GymTVLanding = () => {
  const navigate = useNavigate();
  const [modal, setModal] = useState(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [gymCode, setGymCode] = useState("");
  const [authLoading, setAuthLoading] = useState(false);
  const [authError, setAuthError] = useState("");
  const [form, setForm] = useState({ name: "", businessName: "", email: "", phone: "", message: "" });
  const [formLoading, setFormLoading] = useState(false);
  const [formSuccess, setFormSuccess] = useState(false);
  const [formError, setFormError] = useState("");
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const openModal = (type) => {
    setModal(type);
    setEmail(""); setPassword(""); setGymCode(""); setAuthError("");
  };
  const closeModal = () => setModal(null);

  const handleLogin = async (destination) => {
    setAuthError(""); setAuthLoading(true);
    try {
      const res = await axios.post(`${apiPath.baseURL}${apiPath.login}`, { email, password });
      const data = res.data;
      if (data.status === 200 && data.data?.user?.token) {
        const user = data.data.user;
        localStorage.setItem("accessToken", user.token);
        localStorage.setItem("refresh_token", user.refresh_token);
        localStorage.setItem("user", JSON.stringify(user));
        localStorage.setItem("userProfile", JSON.stringify(user));
        // Master accounts always need Programme view to access push/variant tools,
        // regardless of which login button was clicked. Check before honouring destination.
        let finalDest = destination;
        try {
          const ngRes = await axios.get(apiPath.baseURL + "gym_owner/network/group", {
            headers: { Authorization: "Bearer " + user.token }
          });
          if (ngRes.data?.status === 200 && ngRes.data?.data?.network_group) {
            finalDest = "/programme";
          }
        } catch (_) { /* non-master or network error - keep original destination */ }
        closeModal();
        navigate(finalDest);
      } else {
        setAuthError(data.message || "Login failed");
      }
    } catch (err) {
      setAuthError(err.response?.data?.message || "Invalid email or password");
    } finally {
      setAuthLoading(false);
    }
  };

  const handleCoachView = () => {
    const code = gymCode.trim().toUpperCase();
    if (!code) { setAuthError("Please enter your gym code"); return; }
    closeModal();
    navigate(`/view/${code}`);
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) { setFormError("Name and email are required"); return; }
    setFormLoading(true); setFormError("");
    try {
      await axios.post(`${PORTAL_BASE}/gymtv-inquiry`, form);
      setFormSuccess(true);
      setForm({ name: "", businessName: "", email: "", phone: "", message: "" });
    } catch {
      setFormError("Failed to submit. Please try again.");
    } finally {
      setFormLoading(false);
    }
  };

  const s = styles;

  return (
    <div style={s.page}>
      {/* ── NAV ── */}
      <nav style={{ ...s.nav, ...(scrolled ? s.navScrolled : {}) }}>
        <div style={s.navInner}>
          <img src={logoImg} alt="FusionHub GymTV" style={s.navLogo} />
          <div style={s.navCenter}>
            <a href="#features" style={s.navLink}>Features</a>
            <a href="#app" style={s.navLink}>App</a>
            <a href="#get-started" style={s.navLink}>Get Started</a>
          </div>
          <button style={s.navCta} onClick={() => openModal("dashboard")}>Log in</button>
        </div>
      </nav>

      {/* ── HERO ── */}
      <section style={s.hero}>
        {/* Background grid */}
        <div style={s.heroBgGrid} />
        <div style={s.heroBgGlow} />

        <div style={s.heroInner}>
          {/* Logo */}
          <div style={s.logoWrap}>
            <div style={s.logoGlow} />
            <div style={s.logoBox}>
              <img src={logoImg} alt="FusionHub" style={s.logoImg} />
            </div>
          </div>

          <div style={s.heroBadge}>GymTV by FusionHub Studios</div>

          <h1 style={s.heroH1}>
            <span style={s.heroH1Line1}>The workout display</span>
            <span style={{ ...s.heroH1Line1, ...s.heroH1Accent }}>platform for gyms</span>
            <span style={s.heroH1Line1}>that mean business.</span>
          </h1>

          <p style={s.heroSub}>
            Real-time sync. 8 display modes. Native Android TV app.<br />
            Coach controls from any device. No hardware lock-in.
          </p>

          {/* 3 Access Cards */}
          <div style={s.accessGrid}>
            <button style={{ ...s.accessCard, ...s.accessCardPrimary }} onClick={() => openModal("dashboard")}>
              <div style={s.accessIcon}><Icon d={ICONS.bar} size={22} color="#fff" /></div>
              <div style={s.accessCardText}>
                <span style={s.accessCardTitle}>Gym Dashboard</span>
                <span style={s.accessCardSub}>Control displays & manage programmes</span>
              </div>
              <Icon d={ICONS.arrow} size={16} color="rgba(255,255,255,0.5)" />
            </button>

            <button style={{ ...s.accessCard, ...s.accessCardSecondary }} onClick={() => openModal("programme")}>
              <div style={{ ...s.accessIcon, background: "rgba(255,255,255,0.08)" }}><Icon d={ICONS.calendar} size={22} color="#e8e8ff" /></div>
              <div style={s.accessCardText}>
                <span style={s.accessCardTitle}>Programme View</span>
                <span style={s.accessCardSub}>Weekly workout schedule</span>
              </div>
              <Icon d={ICONS.arrow} size={16} color="rgba(255,255,255,0.3)" />
            </button>

            <button style={{ ...s.accessCard, ...s.accessCardTertiary }} onClick={() => openModal("coach")}>
              <div style={{ ...s.accessIcon, background: "rgba(255,255,255,0.05)" }}><Icon d={ICONS.eye} size={22} color="#9ca3af" /></div>
              <div style={s.accessCardText}>
                <span style={s.accessCardTitle}>Coach View</span>
                <span style={{ ...s.accessCardSub, color: "#6b7280" }}>No login. Just your gym code.</span>
              </div>
              <Icon d={ICONS.arrow} size={16} color="rgba(255,255,255,0.2)" />
            </button>
          </div>
        </div>

        {/* Scroll hint */}
        <div style={s.scrollHint}>
          <div style={s.scrollDot} />
        </div>
      </section>

      {/* ── STATS ── */}
      <section style={s.statsBar}>
        <div style={s.statsInner}>
          {STATS.map((st, i) => (
            <div key={i} style={s.statItem}>
              <span style={s.statValue}>{st.value}</span>
              <span style={s.statLabel}>{st.label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* ── FEATURES ── */}
      <section id="features" style={s.section}>
        <div style={s.sectionInner}>
          <p style={s.sectionTag}>Built for boutique fitness</p>
          <h2 style={s.sectionTitle}>Everything you need on screen</h2>
          <p style={s.sectionSub}>
            GymTV is purpose-built for class-based training. Every display mode, every timer format, every layout, designed around how real coaching sessions actually run.
          </p>
          <div style={s.featureGrid}>
            {FEATURES.map((f, i) => (
              <div key={i} style={s.featureCard}>
                <div style={s.featureIconWrap}>
                  <Icon d={ICONS[f.icon]} size={20} color="#1919ce" />
                </div>
                <h3 style={s.featureCardTitle}>{f.title}</h3>
                <p style={s.featureCardDesc}>{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── APP SECTION ── */}
      <section id="app" style={s.appSection}>
        <div style={s.appInner}>
          <div style={s.appContent}>
            <p style={s.sectionTag}>Native Android TV App</p>
            <h2 style={{ ...s.sectionTitle, textAlign: "left", marginBottom: 16 }}>
              Built for your TV.<br />Not your browser.
            </h2>
            <p style={{ ...s.sectionSub, textAlign: "left", marginBottom: 32 }}>
              The GymTV app is a native Flutter application built specifically for Android TV and Amazon Fire TV. Install it once and your displays are always ready. No browser, no refresh, no fuss.
            </p>
            <ul style={s.appList}>
              {APP_FEATURES.map((feat, i) => (
                <li key={i} style={s.appListItem}>
                  <div style={s.appCheckIcon}><Icon d={ICONS.check} size={14} color="#1919ce" /></div>
                  <span style={{ color: "#d1d5db", fontSize: 15 }}>{feat}</span>
                </li>
              ))}
            </ul>
            <div style={s.appBadges}>
              <div style={s.appBadge}>
                <Icon d={ICONS.tv} size={18} color="#9ca3af" />
                <span style={{ color: "#9ca3af", fontSize: 13, fontWeight: 600 }}>Android TV</span>
              </div>
              <div style={s.appBadge}>
                <Icon d={ICONS.zap} size={18} color="#9ca3af" />
                <span style={{ color: "#9ca3af", fontSize: 13, fontWeight: 600 }}>Amazon Fire TV</span>
              </div>
              <div style={s.appBadge}>
                <Icon d={ICONS.globe} size={18} color="#9ca3af" />
                <span style={{ color: "#9ca3af", fontSize: 13, fontWeight: 600 }}>Any Smart TV</span>
              </div>
            </div>
          </div>

          {/* App visual */}
          <div style={s.appVisual}>
            <div style={s.tvFrame}>
              <div style={s.tvScreen}>
                <div style={s.tvTopBar}>
                  <span style={{ color: "rgba(255,255,255,0.4)", fontSize: 11, fontWeight: 700, letterSpacing: 2 }}>FUSIONHUB GYMTV</span>
                  <span style={{ color: "#1919ce", fontSize: 11, fontWeight: 700, display: "flex", alignItems: "center", gap: 4 }}>
                    <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#22c55e", display: "inline-block" }} /> LIVE
                  </span>
                </div>
                <div style={s.tvContent}>
                  <div style={s.tvBlock}>
                    <div style={s.tvBlockLabel}>BLOCK 1: STRENGTH</div>
                    <div style={s.tvBlockTimer}>04:32</div>
                    <div style={s.tvExercise}><span style={s.tvExName}>Back Squat</span><span style={s.tvExReps}>5 × 5 @ 80%</span></div>
                    <div style={s.tvExercise}><span style={s.tvExName}>Romanian Deadlift</span><span style={s.tvExReps}>4 × 8</span></div>
                    <div style={s.tvExercise}><span style={s.tvExName}>Goblet Squat</span><span style={s.tvExReps}>3 × 12</span></div>
                  </div>
                  <div style={s.tvVariantRow}>
                    <div style={s.tvVariant}>A <span style={{ color: "#9ca3af", fontWeight: 400 }}>RX</span></div>
                    <div style={{ ...s.tvVariant, background: "rgba(25,25,206,0.3)", border: "1px solid rgba(25,25,206,0.5)", color: "#818cf8" }}>B <span style={{ color: "#818cf8", fontWeight: 400 }}>SCALED</span></div>
                    <div style={s.tvVariant}>C <span style={{ color: "#9ca3af", fontWeight: 400 }}>BEGINNER</span></div>
                  </div>
                </div>
              </div>
              <div style={s.tvStand} />
              <div style={s.tvBase} />
            </div>
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS ── */}
      <section style={s.section}>
        <div style={s.sectionInner}>
          <p style={s.sectionTag}>Simple setup</p>
          <h2 style={s.sectionTitle}>Running in under 10 minutes</h2>
          <div style={s.stepsGrid}>
            {[
              { n: "01", icon: "tv", title: "Connect your screen", desc: "Install the GymTV app on any Android TV or Amazon Fire Stick. Open gym.fusionhub.studio on any device as a backup." },
              { n: "02", icon: "calendar", title: "Build your programme", desc: "Set up your weekly workout programme in the dashboard. Configure blocks, exercises, timers, and A/B/C variants for each class." },
              { n: "03", icon: "wifi", title: "Hit Start and sync instantly", desc: "Tap START and every connected TV syncs in under 50ms. Control pace, switch blocks and trigger displays from your phone mid-class." },
            ].map((step, i) => (
              <div key={i} style={s.stepCard}>
                <div style={s.stepNum}>{step.n}</div>
                <div style={s.stepIconWrap}><Icon d={ICONS[step.icon]} size={20} color="#1919ce" /></div>
                <h3 style={s.stepTitle}>{step.title}</h3>
                <p style={s.stepDesc}>{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── LEAD FORM ── */}
      <section id="get-started" style={{ ...s.section, background: "#05050d", borderTop: "1px solid rgba(255,255,255,0.06)" }}>
        <div style={{ ...s.sectionInner, maxWidth: 640 }}>
          <p style={s.sectionTag}>Get GymTV</p>
          <h2 style={s.sectionTitle}>Ready to upgrade your gym display?</h2>
          <p style={{ color: "#6b7280", fontSize: 15, textAlign: "center", marginBottom: 40, lineHeight: 1.7 }}>
            Leave your details and the FusionHub team will reach out to get you set up. No commitment required.
          </p>

          {formSuccess ? (
            <div style={s.successBox}>
              <div style={s.successCheck}><Icon d={ICONS.check} size={28} color="#22c55e" /></div>
              <h3 style={{ color: "#fff", margin: "0 0 8px", fontSize: 20, fontWeight: 800 }}>You're on the list.</h3>
              <p style={{ color: "#6b7280", margin: 0, fontSize: 14 }}>Someone from the FusionHub team will be in touch shortly.</p>
            </div>
          ) : (
            <form onSubmit={handleFormSubmit} style={s.leadForm}>
              <div style={s.formRow}>
                <input style={s.input} placeholder="Your name *" value={form.name}
                  onChange={e => setForm(p => ({ ...p, name: e.target.value }))} required />
                <input style={s.input} placeholder="Studio or business name" value={form.businessName}
                  onChange={e => setForm(p => ({ ...p, businessName: e.target.value }))} />
              </div>
              <div style={s.formRow}>
                <input style={s.input} type="email" placeholder="Email address *" value={form.email}
                  onChange={e => setForm(p => ({ ...p, email: e.target.value }))} required />
                <input style={s.input} type="tel" placeholder="Phone number" value={form.phone}
                  onChange={e => setForm(p => ({ ...p, phone: e.target.value }))} />
              </div>
              <textarea style={{ ...s.input, height: 90, resize: "vertical" }}
                placeholder="Tell us about your gym (optional)"
                value={form.message} onChange={e => setForm(p => ({ ...p, message: e.target.value }))} />
              {formError && <p style={s.errorText}>{formError}</p>}
              <button type="submit" style={s.submitBtn} disabled={formLoading}>
                {formLoading ? "Sending..." : <>Get in touch <Icon d={ICONS.arrow} size={16} color="#fff" /></>}
              </button>
            </form>
          )}
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer style={s.footer}>
        <div style={s.footerInner}>
          <img src={logoImg} alt="FusionHub" style={{ height: 32 }} />
          <p style={s.footerText}>
            © 2026 FusionHub Studios ·{" "}
            <a href="https://portal.fusionhub.studio" style={s.footerLink}>portal.fusionhub.studio</a>
            {" "}·{" "}
            <a href="/privacy-policy" style={s.footerLink}>Privacy</a>
            {" "}·{" "}
            <a href="/terms-and-conditions" style={s.footerLink}>Terms</a>
          </p>
        </div>
      </footer>

      {/* ── LOGIN MODAL ── */}
      {modal && (
        <div style={s.overlay} onClick={closeModal}>
          <div style={s.modalBox} onClick={e => e.stopPropagation()}>
            <button style={s.closeBtn} onClick={closeModal} aria-label="Close">
              <Icon d={ICONS.close} size={18} color="#6b7280" />
            </button>

            <div style={s.modalLogoWrap}>
              <div style={s.modalLogoGlow} />
              <div style={s.modalLogoBox}>
                <img src={logoImg} alt="FusionHub" style={{ height: 36, position: "relative" }} />
              </div>
            </div>

            {modal === "coach" ? (
              <>
                <h2 style={s.modalTitle}>Coach View</h2>
                <p style={s.modalSub}>Enter your 6-digit coach PIN to view the weekly programme.</p>
                <input style={s.modalInput} placeholder=""
                  value={gymCode} onChange={e => setGymCode(e.target.value.toUpperCase())}
                  onKeyDown={e => e.key === "Enter" && handleCoachView()} autoFocus maxLength={6} />
                {authError && <p style={s.errorText}>{authError}</p>}
                <button style={s.modalBtn} onClick={handleCoachView}>
                  View Programme <Icon d={ICONS.arrow} size={16} color="#fff" />
                </button>
              </>
            ) : (
              <>
                <h2 style={s.modalTitle}>
                  {modal === "dashboard" ? "Gym Dashboard" : "Programme"}
                </h2>
                <p style={s.modalSub}>Sign in with your FusionHub credentials</p>
                <input style={s.modalInput} type="email" placeholder="Email address"
                  value={email} onChange={e => setEmail(e.target.value)} autoFocus />
                <input style={s.modalInput} type="password" placeholder="Password"
                  value={password} onChange={e => setPassword(e.target.value)}
                  onKeyDown={e => e.key === "Enter" && handleLogin(modal === "dashboard" ? "/dashboard" : "/programme")} />
                {authError && <p style={s.errorText}>{authError}</p>}
                <button style={s.modalBtn}
                  onClick={() => handleLogin(modal === "dashboard" ? "/dashboard" : "/programme")}
                  disabled={authLoading}>
                  {authLoading ? "Signing in..." : <span style={{ display: "flex", alignItems: "center", gap: 8 }}>Sign in <Icon d={ICONS.arrow} size={16} color="#fff" /></span>}
                </button>
              </>
            )}

            <div style={s.modalDivider} />
            <p style={{ color: "#4b5563", fontSize: 13, textAlign: "center", margin: 0 }}>
              Need access?{" "}
              <a href="#get-started" style={{ color: "#6366f1", textDecoration: "none" }} onClick={closeModal}>
                Get in touch with FusionHub
              </a>
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

// ── DESIGN TOKENS ──────────────────────────────────────────────────────────────
const BG       = "#080810";
const SURFACE  = "#0d0d1a";
const CARD     = "#111120";
const BORDER   = "rgba(255,255,255,0.07)";
const BRAND    = "#1919ce";
const BRANDLT  = "rgba(25,25,206,0.15)";
const MUTED    = "#6b7280";
const SUBTLE   = "#9ca3af";

const styles = {
  page: { background: BG, minHeight: "100vh", color: "#fff", fontFamily: "'Inter', 'Helvetica Neue', sans-serif", overflowX: "hidden" },

  // Nav
  nav: { position: "sticky", top: 0, zIndex: 100, transition: "background 0.3s, box-shadow 0.3s" },
  navScrolled: { background: "rgba(8,8,16,0.95)", backdropFilter: "blur(16px)", boxShadow: "0 1px 0 rgba(255,255,255,0.07)" },
  navInner: { display: "flex", alignItems: "center", justifyContent: "space-between", maxWidth: 1100, margin: "0 auto", padding: "16px 24px" },
  navLogo: { height: 32 },
  navCenter: { display: "flex", gap: 32 },
  navLink: { color: SUBTLE, textDecoration: "none", fontSize: 14, fontWeight: 500, transition: "color 0.2s" },
  navCta: { background: BRAND, color: "#fff", border: "none", borderRadius: 10, padding: "9px 20px", fontWeight: 700, fontSize: 14, cursor: "pointer", letterSpacing: 0.2 },

  // Hero
  hero: { position: "relative", minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "80px 24px 60px", overflow: "hidden" },
  heroBgGrid: {
    position: "absolute", inset: 0, zIndex: 0,
    backgroundImage: `linear-gradient(${BORDER} 1px, transparent 1px), linear-gradient(90deg, ${BORDER} 1px, transparent 1px)`,
    backgroundSize: "60px 60px",
    maskImage: "radial-gradient(ellipse 70% 70% at 50% 40%, black 40%, transparent 100%)",
    WebkitMaskImage: "radial-gradient(ellipse 70% 70% at 50% 40%, black 40%, transparent 100%)",
  },
  heroBgGlow: {
    position: "absolute", top: "20%", left: "50%", transform: "translateX(-50%)",
    width: 600, height: 400, borderRadius: "50%",
    background: `radial-gradient(ellipse, ${BRANDLT} 0%, transparent 70%)`,
    filter: "blur(80px)", zIndex: 0,
  },
  heroInner: { position: "relative", zIndex: 1, maxWidth: 820, width: "100%", margin: "0 auto", textAlign: "center" },

  // Logo
  logoWrap: { position: "relative", display: "inline-flex", alignItems: "center", justifyContent: "center", marginBottom: 32 },
  logoGlow: { position: "absolute", inset: 0, borderRadius: "50%", background: `radial-gradient(circle, rgba(25,25,206,0.5) 0%, transparent 70%)`, filter: "blur(40px)", transform: "scale(2.5)", zIndex: 0 },
  logoBox: { position: "relative", zIndex: 1, background: "rgba(255,255,255,0.06)", border: `1px solid rgba(255,255,255,0.12)`, borderRadius: 20, padding: "20px 40px", boxShadow: `0 0 60px rgba(25,25,206,0.35), 0 20px 40px rgba(0,0,0,0.5)` },
  logoImg: { height: 56, width: "auto", filter: "drop-shadow(0 0 20px rgba(25,25,206,0.7))" },

  // Hero text
  heroBadge: { display: "inline-block", background: BRANDLT, border: `1px solid rgba(25,25,206,0.3)`, color: "#818cf8", borderRadius: 20, padding: "5px 16px", fontSize: 12, fontWeight: 700, letterSpacing: 1.5, textTransform: "uppercase", marginBottom: 24 },
  heroH1: { margin: "0 0 24px", lineHeight: 1.1, padding: 0 },
  heroH1Line1: { display: "block", fontSize: "clamp(32px, 5.5vw, 60px)", fontWeight: 900, color: "#e8e8ff", letterSpacing: -1 },
  heroH1Accent: { background: `linear-gradient(135deg, #818cf8 0%, ${BRAND} 100%)`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text" },
  heroSub: { color: SUBTLE, fontSize: "clamp(15px, 2vw, 17px)", lineHeight: 1.7, maxWidth: 540, margin: "0 auto 48px" },

  // Access cards
  accessGrid: { display: "flex", flexDirection: "column", gap: 10, maxWidth: 560, margin: "0 auto" },
  accessCard: { display: "flex", alignItems: "center", gap: 14, padding: "16px 20px", borderRadius: 14, border: "none", cursor: "pointer", width: "100%", textAlign: "left", transition: "transform 0.15s, box-shadow 0.15s" },
  accessCardPrimary: { background: BRAND, boxShadow: `0 8px 32px rgba(25,25,206,0.4)` },
  accessCardSecondary: { background: CARD, border: `1px solid rgba(25,25,206,0.25)` },
  accessCardTertiary: { background: "rgba(255,255,255,0.03)", border: `1px solid ${BORDER}` },
  accessIcon: { width: 40, height: 40, borderRadius: 10, background: "rgba(255,255,255,0.15)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
  accessCardText: { flex: 1, display: "flex", flexDirection: "column", gap: 2 },
  accessCardTitle: { fontSize: 15, fontWeight: 700, color: "#fff" },
  accessCardSub: { fontSize: 12, color: "rgba(255,255,255,0.55)", fontWeight: 400 },

  // Scroll hint
  scrollHint: { position: "absolute", bottom: 32, left: "50%", transform: "translateX(-50%)" },
  scrollDot: { width: 6, height: 6, borderRadius: "50%", background: MUTED, animation: "none" },

  // Stats bar
  statsBar: { borderTop: `1px solid ${BORDER}`, borderBottom: `1px solid ${BORDER}`, background: SURFACE },
  statsInner: { maxWidth: 900, margin: "0 auto", padding: "28px 24px", display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16 },
  statItem: { display: "flex", flexDirection: "column", alignItems: "center", gap: 4 },
  statValue: { fontSize: "clamp(22px, 3vw, 32px)", fontWeight: 900, color: "#fff", letterSpacing: -0.5 },
  statLabel: { fontSize: 12, color: MUTED, fontWeight: 500, textAlign: "center" },

  // Sections
  section: { padding: "80px 24px", background: BG },
  sectionInner: { maxWidth: 1080, margin: "0 auto" },
  sectionTag: { color: "#6366f1", fontSize: 11, fontWeight: 700, letterSpacing: 2.5, textTransform: "uppercase", textAlign: "center", marginBottom: 14, display: "block" },
  sectionTitle: { fontSize: "clamp(26px, 3.5vw, 42px)", fontWeight: 900, textAlign: "center", margin: "0 0 16px", lineHeight: 1.15, color: "#f0f0ff" },
  sectionSub: { color: SUBTLE, fontSize: 16, textAlign: "center", maxWidth: 600, margin: "0 auto 56px", lineHeight: 1.7 },

  // Feature grid
  featureGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: 2 },
  featureCard: { background: CARD, border: `1px solid ${BORDER}`, padding: "28px", transition: "border-color 0.2s" },
  featureIconWrap: { width: 40, height: 40, borderRadius: 10, background: BRANDLT, border: `1px solid rgba(25,25,206,0.3)`, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 16 },
  featureCardTitle: { fontSize: 16, fontWeight: 800, margin: "0 0 10px", color: "#e8e8ff" },
  featureCardDesc: { color: SUBTLE, fontSize: 14, lineHeight: 1.7, margin: 0 },

  // App section
  appSection: { padding: "80px 24px", background: SURFACE, borderTop: `1px solid ${BORDER}`, borderBottom: `1px solid ${BORDER}` },
  appInner: { maxWidth: 1080, margin: "0 auto", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 80, alignItems: "center" },
  appContent: { paddingRight: 16 },
  appList: { listStyle: "none", padding: 0, margin: "0 0 32px", display: "flex", flexDirection: "column", gap: 12 },
  appListItem: { display: "flex", alignItems: "flex-start", gap: 12 },
  appCheckIcon: { width: 24, height: 24, borderRadius: 6, background: BRANDLT, border: `1px solid rgba(25,25,206,0.3)`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 1 },
  appBadges: { display: "flex", flexWrap: "wrap", gap: 10 },
  appBadge: { display: "flex", alignItems: "center", gap: 8, background: "rgba(255,255,255,0.04)", border: `1px solid ${BORDER}`, borderRadius: 8, padding: "8px 14px" },

  // TV Visual
  appVisual: { display: "flex", justifyContent: "center" },
  tvFrame: { display: "flex", flexDirection: "column", alignItems: "center", filter: "drop-shadow(0 40px 80px rgba(0,0,0,0.8))" },
  tvScreen: { width: 360, background: "#05050d", borderRadius: "16px 16px 4px 4px", border: `2px solid rgba(255,255,255,0.12)`, overflow: "hidden", boxShadow: `0 0 0 1px ${BORDER}, 0 20px 60px rgba(25,25,206,0.2)` },
  tvTopBar: { display: "flex", justifyContent: "space-between", padding: "10px 16px", borderBottom: `1px solid ${BORDER}`, background: "rgba(25,25,206,0.08)" },
  tvContent: { padding: "20px 16px 16px" },
  tvBlock: { marginBottom: 16 },
  tvBlockLabel: { fontSize: 10, fontWeight: 700, letterSpacing: 2, color: "#6366f1", marginBottom: 8 },
  tvBlockTimer: { fontSize: 48, fontWeight: 900, color: "#fff", lineHeight: 1, marginBottom: 12, fontVariantNumeric: "tabular-nums" },
  tvExercise: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderBottom: `1px solid ${BORDER}` },
  tvExName: { fontSize: 13, fontWeight: 600, color: "#e8e8ff" },
  tvExReps: { fontSize: 12, color: "#6366f1", fontWeight: 700 },
  tvVariantRow: { display: "flex", gap: 6, marginTop: 12 },
  tvVariant: { flex: 1, padding: "6px 8px", background: "rgba(255,255,255,0.05)", border: `1px solid ${BORDER}`, borderRadius: 6, fontSize: 11, fontWeight: 700, color: "#9ca3af", textAlign: "center" },
  tvStand: { width: 40, height: 20, background: "rgba(255,255,255,0.08)", clipPath: "polygon(30% 0%, 70% 0%, 80% 100%, 20% 100%)" },
  tvBase: { width: 100, height: 6, background: "rgba(255,255,255,0.06)", borderRadius: 3 },

  // Steps
  stepsGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 40 },
  stepCard: { paddingTop: 8 },
  stepNum: { fontSize: 52, fontWeight: 900, color: "rgba(25,25,206,0.2)", lineHeight: 1, marginBottom: 16 },
  stepIconWrap: { width: 36, height: 36, borderRadius: 8, background: BRANDLT, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 14 },
  stepTitle: { fontSize: 18, fontWeight: 800, margin: "0 0 10px", color: "#e8e8ff" },
  stepDesc: { color: SUBTLE, fontSize: 14, lineHeight: 1.7, margin: 0 },

  // Lead form
  leadForm: { display: "flex", flexDirection: "column", gap: 12 },
  formRow: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 },
  input: { background: "rgba(255,255,255,0.04)", border: `1px solid ${BORDER}`, borderRadius: 10, padding: "13px 16px", color: "#fff", fontSize: 14, width: "100%", boxSizing: "border-box", outline: "none", fontFamily: "inherit", transition: "border-color 0.2s" },
  submitBtn: { display: "flex", alignItems: "center", justifyContent: "center", gap: 10, background: BRAND, color: "#fff", border: "none", borderRadius: 10, padding: "14px 24px", fontWeight: 700, fontSize: 15, cursor: "pointer", marginTop: 4, boxShadow: `0 8px 24px rgba(25,25,206,0.35)`, fontFamily: "inherit" },
  successBox: { background: "rgba(34,197,94,0.08)", border: "1px solid rgba(34,197,94,0.25)", borderRadius: 16, padding: "48px 24px", textAlign: "center" },
  successCheck: { width: 56, height: 56, borderRadius: "50%", background: "rgba(34,197,94,0.15)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" },
  errorText: { color: "#f87171", fontSize: 13, margin: "4px 0 0" },

  // Footer
  footer: { padding: "40px 24px", borderTop: `1px solid ${BORDER}` },
  footerInner: { maxWidth: 1100, margin: "0 auto", display: "flex", flexDirection: "column", alignItems: "center", gap: 12 },
  footerText: { color: MUTED, fontSize: 13, margin: 0, textAlign: "center" },
  footerLink: { color: MUTED, textDecoration: "none" },

  // Modal
  overlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.85)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 16, backdropFilter: "blur(8px)" },
  modalBox: { background: SURFACE, border: `1px solid rgba(255,255,255,0.1)`, borderRadius: 24, padding: "40px 36px", width: "100%", maxWidth: 400, position: "relative", display: "flex", flexDirection: "column", alignItems: "center", boxShadow: "0 40px 80px rgba(0,0,0,0.7)" },
  closeBtn: { position: "absolute", top: 16, right: 16, background: "rgba(255,255,255,0.05)", border: `1px solid ${BORDER}`, borderRadius: 8, width: 32, height: 32, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" },
  modalLogoWrap: { position: "relative", display: "inline-flex", marginBottom: 24 },
  modalLogoGlow: { position: "absolute", inset: 0, borderRadius: "50%", background: `radial-gradient(circle, rgba(25,25,206,0.4) 0%, transparent 70%)`, filter: "blur(20px)", transform: "scale(2)", zIndex: 0 },
  modalLogoBox: { position: "relative", zIndex: 1, background: "rgba(255,255,255,0.05)", border: `1px solid rgba(255,255,255,0.1)`, borderRadius: 14, padding: "12px 24px" },
  modalTitle: { fontSize: 22, fontWeight: 900, margin: "0 0 6px", textAlign: "center", color: "#f0f0ff" },
  modalSub: { color: MUTED, fontSize: 13, textAlign: "center", margin: "0 0 24px", lineHeight: 1.6 },
  modalInput: { background: "rgba(255,255,255,0.05)", border: `1px solid ${BORDER}`, borderRadius: 10, padding: "12px 16px", color: "#fff", fontSize: 14, width: "100%", boxSizing: "border-box", outline: "none", marginBottom: 10, fontFamily: "inherit" },
  modalBtn: { display: "flex", alignItems: "center", justifyContent: "center", gap: 8, background: BRAND, color: "#fff", border: "none", borderRadius: 10, padding: "13px 24px", fontWeight: 700, fontSize: 14, cursor: "pointer", width: "100%", marginTop: 4, fontFamily: "inherit", boxShadow: `0 8px 24px rgba(25,25,206,0.35)` },
  modalDivider: { width: "100%", height: 1, background: BORDER, margin: "20px 0" },
};

export default GymTVLanding;
